import { COLORS } from '@btime/constants'
import { appendIf, capitalizeIfLower, formatDate } from '@btime/helpers'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { RefetchingIndicator } from '../../../../components/helpers/StatePresenter'
import { fromNow } from '../../../../utils/helpers/date'
import { getSemaphoreConfigColor } from '../../../../utils/helpers/semaphore'
import { useConfig } from '../../../../containers/configs/_load/ConfigsLoader'
import { useOpenServiceOrderModal } from '../../../../utils/routes/modals/modalsActions'
import { useRowActionsColumn } from '../../../../components/tables/Table'
import { useServiceOrders } from '../_load/ServiceOrdersLoader'
import { useServiceOrdersActionsList } from '../../../../utils/actions/serviceOrders/list'
import ActionPlansTable from './ActionPlansTable'
import AssetPresenter from '../../../../containers/assets/AssetPresenter'
import Badge from '../../../../packages/kiti/components/presentation/Badge'
import DataTable from '../../../../packages/kiti/components/table/DataTable'
import EditableServiceOrdersUser from '../../../../containers/serviceOrders/EditableServiceOrdersUser'
import IconCount from '../../../../components/shapes/IconCount'
import Link from '../../../../components/actions/Link'
import PlacePresenter from '../../../../containers/places/PlacePresenter'
import StatusTag, { PRIORITY } from '../../../../containers/serviceOrders/StatusTag'
import Text, { SmallText } from '../../../../packages/kiti/components/presentation/Text'
import ListCounter from '../ListCounter'
import moment from 'moment'

const STATUS_OPEN_AT = ['open', 'received', 'execution', 'pause', 'pending']

const ServiceOrdersTable = ({ customData, ...props }) => {
  const { t } = useTranslation('processes')
  const semaphoreConfig = useConfig('service_order_started_since_colors')

  const onlySelfServiceOrders = useSessionPermission('ownedSOOnlyServiceOrder')
  const hidePlace = useSessionPermission('hideServiceOrdersPlace')

  let data = useServiceOrders()
  const openDetailsModal = useOpenServiceOrderModal()
  data = customData || data

  const columns = [
    {
      label: t('processes.table.code'),
      dataIndex: 'yearlyId',
      sortIndex: 'YEARLY_ID',
      flex: 4,
      headerSideContent: (
        <>
          {!onlySelfServiceOrders && <ListCounter />}
          <RefetchingIndicator />
        </>
      ),
      render: (value, record) => {
        const checklistName = capitalizeIfLower(record.checklist?.name)
        const name = record.name

        return (
          <Link onClick={() => openDetailsModal(record)} block>
            <SmallText size="xsmall" color={COLORS.dark3}>
              {record.yearlyId}
            </SmallText>
            <Text size="h6" bold color={COLORS.dark2} breakWord>
              {name || checklistName}
            </Text>

            {!!name && (
              <SmallText size="xxsmall" color={COLORS.dark3} breakWord>
                {checklistName}
              </SmallText>
            )}
          </Link>
        )
      }
    },

    {
      label: '',
      dataIndex: 'childrenCount',
      width: 2.5,
      render: (value, record, index, { toggleExpand }) => (
        <IconCount value={value} onClick={toggleExpand} icon="BsListTask" />
      ),
      renderExpand: record => <ActionPlansTable serviceOrder={record} />
    },

    {
      label: t('processes.table.responsible'),
      dataIndex: 'user',
      sortIndex: 'USER_NAME',
      render: (_, record) => <EditableServiceOrdersUser serviceOrder={record} />,
      flex: 3
    },

    ...appendIf(!hidePlace, {
      label: t('processes.table.destination'),
      dataIndex: 'place',
      sortIndex: 'PLACE_NAME',
      flex: 4,
      render: (item, record) => {
        if (record.asset?.id)
          return (
            <AssetPresenter
              asset={record.asset}
              place={record.place}
              size="small"
              color={COLORS.dark3}
              hideIcon
              breakWord
            />
          )
        return <PlacePresenter place={item} size="small" color={COLORS.dark3} hideIcon breakWord />
      }
    }),

    {
      label: t('processes.table.scheduling'),
      dataIndex: 'scheduling',
      sortIndex: 'SCHEDULING',
      flex: 2,
      size: 'xsmall',
      format: formatDate('DD/MM/YY HH:mm')
    },

    {
      label: t('processes.table.completion'),
      dataIndex: 'endDate',
      sortIndex: 'END_DATE',
      flex: 2,
      size: 'xsmall',
      format: formatDate('DD/MM/YY HH:mm')
    },

    {
      label: t('processes.table.status'),
      dataIndex: 'status',
      sortIndex: 'STATUS_ID',
      flex: 1.5,
      render: (value, record) => <StatusTag item={value} />
    },

    {
      label: t('processes.table.priority'),
      dataIndex: ['priority', 'name'],
      sortIndex: 'PRIORITY_ID',
      flex: 1.5,
      render: (value, record) => <StatusTag item={record.priority} type={PRIORITY} />
    },

    {
      label: t('processes.table.openedAt'),
      dataIndex: 'insertedAt',
      sortIndex: false,
      flex: 1.5,
      size: 'xsmall',
      format: (value, record) => {
        if (STATUS_OPEN_AT.includes(record.status?.name)) {
          return fromNow(value, { breakpoints: { hour: 48 }, hidePrefix: true })
        }

        return moment(value).format('L LT')
      },
      render: (value, record) => {
        const color = getSemaphoreConfigColor(record.insertedAt, semaphoreConfig?.value)
        return (
          <Badge color={value && color}>
            <Text>{value}</Text>
          </Badge>
        )
      }
    },

    {
      label: t('actions', { ns: 'common' }),
      width: 2.5,
      render: useRowActionsColumn(useServiceOrdersActionsList)
    }
  ]

  return <DataTable rowKey="id" columns={columns} size="medium" data={data} inPanel {...props} />
}

export default ServiceOrdersTable
